import type { TicketStatus as UiTicketStatus } from "@/components/shared/StatusChip";

import { apiRequest } from "@/lib/api";

export type ApiTicketType = "CHANGE_ORDER" | "TM_TICKET" | "EXTRA_WORK";
export type ApprovalDeliveryMethod = "EMAIL" | "SMS" | "EMAIL_AND_SMS";
export const API_TICKET_STATUSES = [
  "DRAFT",
  "PENDING_APPROVAL",
  "APPROVED",
  "REJECTED",
  "NEEDS_CLARIFICATION",
  "ARCHIVED",
] as const;
export type ApiTicketStatus = (typeof API_TICKET_STATUSES)[number];
export const APPROVAL_QUEUE_STATUSES: ApiTicketStatus[] = [
  "PENDING_APPROVAL",
  "NEEDS_CLARIFICATION",
  "REJECTED",
];

export type ProjectTicketContext = {
  projectId: string;
  hasEstimate: boolean;
  estimateTicketId: string | null;
  estimateStatus: ApiTicketStatus | null;
  estimateTotal: number;
  approvedTotal: number;
  approvedEstimateTotal: number;
  approvedChangeOrderAdditionsTotal: number;
  approvedChangeOrderDeductionsTotal: number;
  approvedExtraWorkTotal: number;
  currentContractTotal: number;
  pendingChangesTotal: number;
  changeOrderCount: number;
  extraWorkCount: number;
  lastTicketCreatedAt: string | null;
  draftByType: Partial<Record<ApiTicketType, { id: string; status: ApiTicketStatus; createdAt: string }>>;
  tickets: Array<{
    id: string;
    type: ApiTicketType;
    status: ApiTicketStatus;
    createdAt: string;
    totalAmount: number;
    title?: string | null;
  }>;
};

export function mapApiTypeToLabel(type: ApiTicketType) {
  if (type === "CHANGE_ORDER") return "Change Order";
  if (type === "TM_TICKET") return "Estimate";
  return "Extra Work";
}

export function mapUiTypeToApi(type: "co" | "tm" | "ew"): ApiTicketType {
  if (type === "co") return "CHANGE_ORDER";
  if (type === "tm") return "TM_TICKET";
  return "EXTRA_WORK";
}

export function mapApiStatusToUi(status: ApiTicketStatus): UiTicketStatus {
  if (status === "DRAFT") return "draft";
  if (status === "PENDING_APPROVAL") return "pending";
  if (status === "APPROVED") return "approved";
  if (status === "REJECTED") return "rejected";
  if (status === "NEEDS_CLARIFICATION") return "clarification";
  return "archived";
}

export function formatCurrency(value: number | string) {
  const num = typeof value === "string" ? Number(value) : value;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(Number.isFinite(num) ? num : 0);
}

export async function fetchProjectTicketContext(projectId: string) {
  const query = new URLSearchParams();
  query.set("projectId", projectId);
  query.set("pageSize", "100");

  const data = await apiRequest<{
    items: Array<{
      id: string;
      type: ApiTicketType;
      status: ApiTicketStatus;
      createdAt: string;
      totalAmount: string;
      title?: string | null;
    }>;
  }>(`/tickets?${query.toString()}`, { method: "GET" });

  const items = data.items ?? [];
  const estimate = items.find((ticket) => ticket.type === "TM_TICKET") ?? null;
  const approvedChangeOrders = items.filter(
    (ticket) => ticket.type === "CHANGE_ORDER" && ticket.status === "APPROVED",
  );
  const approvedEstimateTotal = items
    .filter((ticket) => ticket.type === "TM_TICKET" && ticket.status === "APPROVED")
    .reduce((sum, ticket) => sum + Number(ticket.totalAmount || 0), 0);
  const approvedExtraWorkTotal = items
    .filter((ticket) => ticket.type === "EXTRA_WORK" && ticket.status === "APPROVED")
    .reduce((sum, ticket) => sum + Number(ticket.totalAmount || 0), 0);
  const approvedChangeOrderAdditionsTotal = approvedChangeOrders
    .filter((ticket) => Number(ticket.totalAmount || 0) >= 0)
    .reduce((sum, ticket) => sum + Number(ticket.totalAmount || 0), 0);
  const approvedChangeOrderDeductionsTotal = Math.abs(
    approvedChangeOrders
      .filter((ticket) => Number(ticket.totalAmount || 0) < 0)
      .reduce((sum, ticket) => sum + Number(ticket.totalAmount || 0), 0),
  );
  const approvedTotal = items
    .filter((ticket) => ticket.status === "APPROVED")
    .reduce((sum, ticket) => sum + Number(ticket.totalAmount || 0), 0);
  const pendingChangesTotal = items
    .filter((ticket) => ticket.status === "PENDING_APPROVAL")
    .reduce((sum, ticket) => sum + Number(ticket.totalAmount || 0), 0);

  const draftByType = items.reduce<ProjectTicketContext["draftByType"]>((acc, ticket) => {
    if (ticket.status !== "DRAFT" || acc[ticket.type]) return acc;
    acc[ticket.type] = {
      id: ticket.id,
      status: ticket.status,
      createdAt: ticket.createdAt,
    };
    return acc;
  }, {});

  const lastTicketCreatedAt = items
    .map((ticket) => ticket.createdAt)
    .sort((left, right) => new Date(right).getTime() - new Date(left).getTime())[0] ?? null;

  return {
    projectId,
    hasEstimate: Boolean(estimate),
    estimateTicketId: estimate?.id ?? null,
    estimateStatus: estimate?.status ?? null,
    estimateTotal: estimate ? Number(estimate.totalAmount || 0) : 0,
    approvedTotal,
    approvedEstimateTotal,
    approvedChangeOrderAdditionsTotal,
    approvedChangeOrderDeductionsTotal,
    approvedExtraWorkTotal,
    currentContractTotal:
      approvedEstimateTotal +
      approvedChangeOrderAdditionsTotal -
      approvedChangeOrderDeductionsTotal +
      approvedExtraWorkTotal,
    pendingChangesTotal,
    changeOrderCount: items.filter((ticket) => ticket.type === "CHANGE_ORDER").length,
    extraWorkCount: items.filter((ticket) => ticket.type === "EXTRA_WORK").length,
    lastTicketCreatedAt,
    draftByType,
    tickets: items.map((ticket) => ({
      id: ticket.id,
      type: ticket.type,
      status: ticket.status,
      createdAt: ticket.createdAt,
      totalAmount: Number(ticket.totalAmount || 0),
      title: ticket.title ?? null,
    })),
  } satisfies ProjectTicketContext;
}
